// Deterministic protection score — computed on-device from declarations +
// policies (no AI, no server; 04-architektura §4). Weights chosen so the
// mock household (car+home+life covered, kids NNW + health missing) = 72%.

import type {
  HouseholdDeclarations,
  Policy,
  PolicyType,
  ProtectionArea,
  ProtectionAreaKey,
  ProtectionScore,
} from '@/types/domain';

const WEIGHTS: Record<ProtectionAreaKey, number> = {
  LIFE: 28,
  CAR: 22,
  HOME: 22,
  KIDS_NNW: 14,
  HEALTH: 14,
};

const AREA_POLICY_TYPES: Record<ProtectionAreaKey, PolicyType[]> = {
  CAR: ['CAR_OC', 'CAR_AC'],
  HOME: ['HOME'],
  LIFE: ['LIFE'],
  KIDS_NNW: ['NNW'],
  HEALTH: ['HEALTH'],
};

export function computeProtection(
  declarations: HouseholdDeclarations,
  policies: Policy[],
): ProtectionScore {
  const relevance: Record<ProtectionAreaKey, boolean> = {
    CAR: declarations.hasCar,
    HOME: declarations.ownsHome || declarations.hasMortgage,
    LIFE: true,
    KIDS_NNW: declarations.hasKids,
    HEALTH: true,
  };

  const areas: ProtectionArea[] = (Object.keys(WEIGHTS) as ProtectionAreaKey[]).map((key) => {
    const matching = policies.filter((p) => AREA_POLICY_TYPES[key].includes(p.type));
    return {
      key,
      relevant: relevance[key],
      covered: matching.length > 0,
      policyIds: matching.map((p) => p.id),
    };
  });

  const relevant = areas.filter((a) => a.relevant);
  const total = relevant.reduce((sum, a) => sum + WEIGHTS[a.key], 0);
  const covered = relevant.filter((a) => a.covered).reduce((sum, a) => sum + WEIGHTS[a.key], 0);

  return {
    areas,
    percent: total === 0 ? 100 : Math.round((100 * covered) / total),
    computedAt: new Date().toISOString(),
  };
}
